import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Home() {
  const { user } = useAuth();

  // If user is logged in, redirect them directly to their dashboard
  if (user) {
    return <Navigate to="/my-dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-white">

      {/* ── HERO SECTION ── */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-sky-800 text-white">
        <div className="container mx-auto px-4 py-16 sm:py-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Trust Badge */}
            <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm text-white text-xs font-medium px-4 py-2 rounded-full mb-6 border border-white border-opacity-30">
              Private beta · Built by a teacher in Qatar · Browser-based secure testing
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Create secure online assessments in minutes
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
              ExamLock helps teachers generate quizzes with AI, assign them to students, and monitor exam activity with timestamped integrity logs.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/teacher-register"
                className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-lg shadow-lg transition-all transform hover:scale-105"
              >
                Create Your First Exam Free
              </Link>
              <Link
                to="/features"
                className="px-8 py-4 bg-white bg-opacity-20 backdrop-blur-sm hover:bg-opacity-30 text-white rounded-lg font-semibold text-lg border-2 border-white border-opacity-30 transition-all"
              >
                See How It Works
              </Link>
            </div>

            {/* Hero Bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto text-left">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border border-white border-opacity-20">
                <div className="text-2xl mb-2">🤖</div>
                <div className="text-sm font-medium text-blue-100">AI question generation from topics or pasted lesson notes</div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border border-white border-opacity-20">
                <div className="text-2xl mb-2">🔒</div>
                <div className="text-sm font-medium text-blue-100">Secure browser-based testing</div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border border-white border-opacity-20">
                <div className="text-2xl mb-2">👁️</div>
                <div className="text-sm font-medium text-blue-100">Real-time teacher monitoring</div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border border-white border-opacity-20">
                <div className="text-2xl mb-2">📋</div>
                <div className="text-sm font-medium text-blue-100">Timestamped activity logs</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS SECTION ── */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-gray-900">
              From lesson idea to secure assessment
            </h2>
            <p className="text-center text-gray-600 mb-16 max-w-2xl mx-auto">
              Three simple steps to create, assign, and monitor your assessments
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Create your assessment</h3>
                <p className="text-gray-600 leading-relaxed">
                  Enter a topic, learning objective, or pasted lesson notes. ExamLock helps generate questions for teacher review.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Assign to students</h3>
                <p className="text-gray-600 leading-relaxed">
                  Assign the assessment to a class or share a secure session link/code depending on the workflow.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Monitor and review</h3>
                <p className="text-gray-600 leading-relaxed">
                  Track progress, review submissions, and see timestamped activity logs for key events.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── FOR TEACHERS WHO NEED MORE THAN GOOGLE FORMS ── */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900 text-center">
              For teachers who need more than Google Forms
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Google Forms is simple, but it does not provide a focused secure test environment, real-time activity monitoring, or timestamped integrity logs. ExamLock helps teachers create, assign, monitor, and review assessments in one place.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Google Forms</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Easy quiz creation</li>
                    <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Good for practice and review</li>
                    <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Limited secure monitoring</li>
                    <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Students can easily switch tabs</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-700 mb-3">ExamLock</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>AI-powered question generation</li>
                    <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Secure test environment</li>
                    <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Real-time activity monitoring</li>
                    <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Timestamped integrity logs</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SECURE TESTING WITHOUT MAKING STUDENTS FEEL ATTACKED ── */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
              Secure testing without making students feel attacked
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-3xl mx-auto">
              ExamLock is designed to keep secure assessments calm and transparent. Students are shown clear rules before starting. If they leave fullscreen, they see a calm prompt to return. Teachers receive clear timestamped logs when secure test rules are broken.
            </p>
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <div className="grid sm:grid-cols-3 gap-6 text-left">
                <div>
                  <div className="text-3xl mb-3">📖</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Clear rules upfront</h3>
                  <p className="text-sm text-gray-600">Students see the test rules before starting</p>
                </div>
                <div>
                  <div className="text-3xl mb-3">💬</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Calm prompts</h3>
                  <p className="text-sm text-gray-600">Gentle reminders to return to fullscreen</p>
                </div>
                <div>
                  <div className="text-3xl mb-3">📊</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Transparent logs</h3>
                  <p className="text-sm text-gray-600">Teachers see what happened and when</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── BUILT FOR QATAR AND GCC SCHOOLS ── */}
      <div className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Built for Qatar and GCC schools
            </h2>
            <p className="text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto">
              ExamLock is built with international schools, bilingual classrooms, and mixed curriculum environments in mind, including IB, IGCSE, NGSS, British curriculum, and school-created assessments.
            </p>
          </div>
        </div>
      </div>

      {/* ── PRICING PREVIEW ── */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-gray-900">
            Simple pricing for teachers and schools
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Start with the Free Beta. Upgrade when you need more.
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Beta */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-400 transition-all">
              <h3 className="text-2xl font-bold mb-2 text-gray-900">Free Beta</h3>
              <div className="text-4xl font-bold mb-4 text-gray-900">$0</div>
              <p className="text-gray-600 mb-6">Best for teachers trying ExamLock with a small class</p>
              <ul className="space-y-3 mb-8 text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Up to 10 students</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Limited AI generation</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Secure Test Mode</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Activity logs</li>
              </ul>
              <Link
                to="/teacher-register"
                className="block w-full py-3 bg-blue-600 hover:bg-blue-700 text-white text-center rounded-lg font-semibold transition-all"
              >
                Start Free
              </Link>
            </div>

            {/* Professional */}
            <div className="bg-blue-600 text-white border-2 border-blue-600 rounded-xl p-8 transform scale-105 shadow-xl">
              <div className="inline-block bg-white text-blue-600 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                MOST POPULAR
              </div>
              <h3 className="text-2xl font-bold mb-2">Professional</h3>
              <div className="text-4xl font-bold mb-4">$9.99<span className="text-lg font-normal">/month</span></div>
              <p className="text-blue-100 mb-6">For individual teachers using ExamLock regularly</p>
              <ul className="space-y-3 mb-8 text-blue-50">
                <li className="flex items-start"><span className="text-white mr-2">✓</span>Up to 50 students</li>
                <li className="flex items-start"><span className="text-white mr-2">✓</span>Monthly AI question allowance</li>
                <li className="flex items-start"><span className="text-white mr-2">✓</span>Secure assessments</li>
                <li className="flex items-start"><span className="text-white mr-2">✓</span>Live monitoring</li>
              </ul>
              <Link
                to="/teacher-register"
                className="block w-full py-3 bg-white hover:bg-gray-100 text-blue-600 text-center rounded-lg font-semibold transition-all"
              >
                Start Free
              </Link>
            </div>

            {/* School Plan */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-400 transition-all">
              <h3 className="text-2xl font-bold mb-2 text-gray-900">School Plan</h3>
              <div className="text-4xl font-bold mb-4 text-gray-900">Custom</div>
              <p className="text-gray-600 mb-6">For departments and schools</p>
              <ul className="space-y-3 mb-8 text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Multiple teacher accounts</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Larger student limits</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>Onboarding support</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>DPA available on request</li>
              </ul>
              <Link
                to="/contact"
                className="block w-full py-3 bg-gray-800 hover:bg-gray-900 text-white text-center rounded-lg font-semibold transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/pricing"
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              View full pricing details →
            </Link>
          </div>
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-sky-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Start your first secure assessment today
          </h2>
          <p className="text-lg text-blue-100 mb-10 max-w-2xl mx-auto">
            Join teachers in private beta using ExamLock for faster assessment creation and clearer online exam monitoring.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/teacher-register"
              className="px-8 py-4 bg-white hover:bg-gray-100 text-blue-600 rounded-lg font-semibold text-lg shadow-lg transition-all"
            >
              Start Free
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-blue-700 hover:bg-blue-800 text-white rounded-lg font-semibold text-lg border-2 border-white border-opacity-30 transition-all"
            >
              Contact for School Pilot
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
